<template>
  <div class="device-exclude-page">
    <particle-background />
    
    <!-- 内容区域 -->
    <div class="content-wrapper">
      <!-- 顶部说明栏 -->
      <div class="info-banner">
        <Icon type="md-information-circle" class="info-icon" />
        <div class="info-text">
          <div class="info-title">监控设备排除配置</div>
          <div class="info-desc">配置机器人未安装的设备,监控时将自动忽略这些设备的错误信息</div>
        </div>
      </div>
      
      <!-- 加载状态 -->
      <Spin v-if="loading" fix>
        <div class="loading-text">
          <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
          <div>加载中...</div>
        </div>
      </Spin>
      
      <!-- 设备列表 -->
      <div class="device-list-section">
        <div class="section-header">
          <div class="header-left">
            <Icon type="md-cube" />
            <span class="header-title">设备列表</span>
            <span class="device-count">共 {{ deviceList.length }} 个设备</span>
          </div>
          <div class="header-right">
            <Button @click="handleSelectAll" size="small" class="action-btn">
              <Icon type="md-checkbox-outline" />
              全选
            </Button>
            <Button @click="handleDeselectAll" size="small" class="action-btn">
              <Icon type="md-square-outline" />
              全不选
            </Button>
            <Button @click="handleInvertSelection" size="small" class="action-btn">
              <Icon type="md-swap" />
              反选
            </Button>
          </div>
        </div>
        
        <div class="device-grid">
          <div 
            v-for="device in deviceList" 
            :key="device.deviceId"
            :class="['device-card', { 'excluded': isExcluded(device.deviceId) }]"
            @click="toggleDevice(device.deviceId)"
          >
            <div class="card-header">
              <div class="device-icon-wrapper">
                <Icon type="md-cube" class="device-icon" />
                <div class="status-indicator" :class="{ 'excluded': isExcluded(device.deviceId) }"></div>
              </div>
              <div class="device-info">
                <div class="device-name">{{ device.deviceNameCn }}</div>
                <div class="device-id">ID: {{ device.deviceId }}</div>
              </div>
            </div>
            <div class="card-footer">
              <div class="device-name-en">{{ device.deviceNameEn }}</div>
              <div class="status-badge" :class="{ 'excluded': isExcluded(device.deviceId) }">
                {{ isExcluded(device.deviceId) ? '未安装' : '已安装' }}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 底部操作栏 -->
      <div class="action-bar">
        <div class="summary-info">
          <Icon type="md-stats" />
          <span class="summary-text">
            已选择 <span class="highlight">{{ excludedDevices.size }}</span> 个未安装设备
          </span>
        </div>
        <div class="action-buttons">
          <Button @click="handleReset" size="large" class="reset-btn">
            <Icon type="md-refresh" />
            重置
          </Button>
          <Button @click="handleSave" type="primary" size="large" class="save-btn" :loading="saving">
            <Icon type="md-checkmark" />
            保存配置
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import ParticleBackground from '@/components/ParticleBackground/index.vue'

export default {
  name: 'DeviceExclude',
  components: {
    ParticleBackground
  },
  props: {
    robotCode: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      deviceList: [],
      excludedDevices: new Set(), // 使用 Set 存储被排除的设备ID
      originalExcludedDevices: new Set() // 原始的排除设备列表，用于重置
    }
  },
  watch: {
    robotCode: {
      immediate: true,
      handler(newCode) {
        if (newCode) {
          this.loadData()
        }
      }
    }
  },
  methods: {
    // 加载数据
    async loadData() {
      this.loading = true
      try {
        // 并行加载设备列表和排除配置
        const [deviceRes, excludeRes] = await Promise.all([
          api.getData('system-server/monitor/dict/device/list', this),
          api.getData(`robot-server/robot/device/exclude/getByCode/${this.robotCode}`, this)
        ])
        
        // 处理设备列表
        if (deviceRes.state === 1) {
          this.deviceList = deviceRes.data || []
        } else {
          this.$Message.error(deviceRes.msg || '加载设备列表失败')
          this.deviceList = []
        }
        
        // 处理排除配置
        if (excludeRes.state === 1) {
          const excludedIds = excludeRes.data?.excludedDeviceIds || ''
          this.excludedDevices = new Set(
            excludedIds ? excludedIds.split(',').filter(id => id) : []
          )
          this.originalExcludedDevices = new Set(this.excludedDevices)
        } else {
          this.$Message.error(excludeRes.msg || '加载排除配置失败')
          this.excludedDevices = new Set()
          this.originalExcludedDevices = new Set()
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$Message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },
    
    // 判断设备是否被排除
    isExcluded(deviceId) {
      return this.excludedDevices.has(String(deviceId))
    },
    
    // 切换设备状态
    toggleDevice(deviceId) {
      const id = String(deviceId)
      if (this.excludedDevices.has(id)) {
        this.excludedDevices.delete(id)
      } else {
        this.excludedDevices.add(id)
      }
      // 触发响应式更新
      this.excludedDevices = new Set(this.excludedDevices)
    },
    
    // 全选
    handleSelectAll() {
      this.excludedDevices = new Set(this.deviceList.map(d => String(d.deviceId)))
    },
    
    // 全不选
    handleDeselectAll() {
      this.excludedDevices = new Set()
    },
    
    // 反选
    handleInvertSelection() {
      const allIds = new Set(this.deviceList.map(d => String(d.deviceId)))
      const newExcluded = new Set()
      allIds.forEach(id => {
        if (!this.excludedDevices.has(id)) {
          newExcluded.add(id)
        }
      })
      this.excludedDevices = newExcluded
    },
    
    // 重置
    handleReset() {
      this.excludedDevices = new Set(this.originalExcludedDevices)
      this.$Message.info('已重置为原始配置')
    },
    
    // 保存配置
    async handleSave() {
      this.saving = true
      try {
        const excludedDeviceIds = Array.from(this.excludedDevices).join(',')
        const data = {
          robotCode: this.robotCode,
          excludedDeviceIds: excludedDeviceIds
        }
        
        const res = await api.postData('robot-server/robot/device/exclude/save', data, this)
        console.log(res)
        if (res.code === 200) {
          this.$Message.success('保存成功')
          this.originalExcludedDevices = new Set(this.excludedDevices)
        } else {
          this.$Message.error(res.msg || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$Message.error('保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.device-exclude-page {
  width: 100%;
  height: 100%;
  background: transparent;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.8), rgba(0, 40, 80, 0.6));
  border: 2px solid rgba(0, 240, 255, 0.3);
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  position: relative;
  z-index: 2;
}

.loading-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #00f0ff;
  font-size: 16px;
  
  .spin-icon-load {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// 信息提示栏
.info-banner {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 16px 20px;
  background: rgba(0, 240, 255, 0.1);
  border-bottom: 2px solid rgba(0, 240, 255, 0.3);
  
  .info-icon {
    font-size: 32px;
    color: #00f0ff;
    filter: drop-shadow(0 0 8px rgba(0, 240, 255, 0.6));
  }
  
  .info-text {
    flex: 1;
    
    .info-title {
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      margin-bottom: 4px;
      text-shadow: 0 0 8px rgba(0, 240, 255, 0.6);
    }
    
    .info-desc {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.8);
      line-height: 1.5;
    }
  }
}

// 设备列表区域
.device-list-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid rgba(0, 240, 255, 0.2);
    
    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;
      
      i {
        font-size: 24px;
        color: #00f0ff;
      }
      
      .header-title {
        font-size: 18px;
        font-weight: 700;
        color: #fff;
        text-shadow: 0 0 8px rgba(0, 240, 255, 0.6);
      }
      
      .device-count {
        font-size: 13px;
        color: rgba(0, 240, 255, 0.7);
        padding: 2px 10px;
        background: rgba(0, 240, 255, 0.1);
        border-radius: 12px;
        border: 1px solid rgba(0, 240, 255, 0.3);
      }
    }
    
    .header-right {
      display: flex;
      gap: 8px;
      
      .action-btn {
        background: rgba(0, 30, 60, 0.6);
        border: 1px solid rgba(0, 240, 255, 0.3);
        color: rgba(255, 255, 255, 0.8);
        transition: all 0.3s ease;
        
        &:hover {
          background: rgba(0, 50, 80, 0.8);
          border-color: rgba(0, 240, 255, 0.6);
          color: #00f0ff;
          box-shadow: 0 0 12px rgba(0, 240, 255, 0.3);
        }
      }
    }
  }
  
  .device-grid {
    flex: 1;
    min-height: 0;
    max-height: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    grid-auto-rows: max-content;
    gap: 16px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;
    align-content: start;
    
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 30, 60, 0.3);
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 240, 255, 0.3);
      border-radius: 4px;
      
      &:hover {
        background: rgba(0, 240, 255, 0.5);
      }
    }
  }
}

// 设备卡片
.device-card {
  background: rgba(0, 30, 60, 0.7);
  border: 2px solid rgba(0, 240, 255, 0.3);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(0, 240, 255, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    border-color: rgba(0, 240, 255, 0.6);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 240, 255, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &.excluded {
    background: rgba(255, 107, 107, 0.15);
    border-color: rgba(255, 107, 107, 0.5);
    
    &:hover {
      border-color: rgba(255, 107, 107, 0.8);
      box-shadow: 0 8px 20px rgba(255, 107, 107, 0.3);
    }
  }
  
  .card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    
    .device-icon-wrapper {
      position: relative;
      width: 48px;
      height: 48px;
      background: rgba(0, 240, 255, 0.15);
      border: 2px solid rgba(0, 240, 255, 0.4);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .device-icon {
        font-size: 28px;
        color: #00f0ff;
      }
      
      .status-indicator {
        position: absolute;
        top: -4px;
        right: -4px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #39FF14;
        border: 2px solid rgba(0, 30, 60, 0.9);
        box-shadow: 0 0 10px rgba(57, 255, 20, 0.8);
        animation: pulse 2s ease-in-out infinite;
        
        &.excluded {
          background: #FF6B6B;
          box-shadow: 0 0 10px rgba(255, 107, 107, 0.8);
        }
      }
    }
    
    .device-info {
      flex: 1;
      
      .device-name {
        font-size: 16px;
        font-weight: 700;
        color: #fff;
        margin-bottom: 4px;
        text-shadow: 0 0 5px rgba(0, 240, 255, 0.5);
      }
      
      .device-id {
        font-size: 12px;
        color: rgba(0, 240, 255, 0.7);
        font-family: 'Courier New', monospace;
      }
    }
  }
  
  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 240, 255, 0.2);
    
    .device-name-en {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
      flex: 1;
    }
    
    .status-badge {
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 700;
      background: rgba(57, 255, 20, 0.2);
      color: #39FF14;
      border: 1px solid rgba(57, 255, 20, 0.5);
      
      &.excluded {
        background: rgba(255, 107, 107, 0.2);
        color: #FF6B6B;
        border-color: rgba(255, 107, 107, 0.5);
      }
    }
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
}

// 底部操作栏
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(0, 20, 40, 0.8);
  border-top: 2px solid rgba(0, 240, 255, 0.3);
  
  .summary-info {
    display: flex;
    align-items: center;
    gap: 10px;
    
    i {
      font-size: 24px;
      color: #00f0ff;
    }
    
    .summary-text {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.8);
      
      .highlight {
        font-size: 20px;
        font-weight: 700;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
        margin: 0 4px;
      }
    }
  }
  
  .action-buttons {
    display: flex;
    gap: 12px;
    
    .reset-btn {
      background: rgba(0, 30, 60, 0.6);
      border: 1px solid rgba(0, 240, 255, 0.3);
      color: rgba(255, 255, 255, 0.8);
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(0, 50, 80, 0.8);
        border-color: rgba(0, 240, 255, 0.6);
        color: #fff;
        box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
      }
    }
    
    .save-btn {
      background: linear-gradient(135deg, rgba(0, 240, 255, 0.25), rgba(0, 180, 255, 0.3));
      border: 1px solid #00f0ff;
      color: #00f0ff;
      font-weight: 700;
      box-shadow: 0 0 15px rgba(0, 240, 255, 0.4);
      transition: all 0.3s ease;
      
      &:hover {
        background: linear-gradient(135deg, rgba(0, 240, 255, 0.35), rgba(0, 180, 255, 0.4));
        box-shadow: 0 0 25px rgba(0, 240, 255, 0.6);
        transform: translateY(-2px);
      }
    }
  }
}
</style>
